import {
  type AzureManagementGroup,
  type Condition,
  ConditionOperatorEnum,
  type GcpOrganization,
  type RestoreAwsOrganizationalUnit,
  type SourceAwsOrganizationalUnit,
} from '@repo/api-gw-sdk';

import {
  createCombineCondition,
  isCombineCondition,
  isEmptyCondition,
} from '@/types/advanceFilter';

export const providerParentPropertyName = 'providerParentId';

export interface ProviderParents {
  organization?: SourceAwsOrganizationalUnit | RestoreAwsOrganizationalUnit;
  managementGroup?: AzureManagementGroup;
  gcpOrgOrFolder?: GcpOrganization;
}

/** The id the `providerParentId` filter options use for this parent, or undefined when the parent has no filter. */
export const getProviderParentFilterId = ({
  organization,
  managementGroup,
  gcpOrgOrFolder,
}: ProviderParents): string | undefined => {
  if (organization) {
    return organization.providerOrganizationalUnitId || undefined;
  }
  if (managementGroup) {
    return managementGroup.managementGroupId || managementGroup.id;
  }
  if (gcpOrgOrFolder) {
    return gcpOrgOrFolder.folderId ?? gcpOrgOrFolder.organizationId;
  }
  return undefined;
};

const withoutProviderParent = (condition: Condition): Condition | undefined => {
  if (isCombineCondition(condition)) {
    const conditions = (condition.conditions ?? [])
      .map(withoutProviderParent)
      .filter((child): child is Condition => !!child);
    return conditions.length ? { ...condition, conditions } : undefined;
  }
  return condition.property === providerParentPropertyName
    ? undefined
    : condition;
};

/** Replaces any `providerParentId` condition in the query with one for `parentId`, keeping every other condition. */
export const buildProviderParentQuery = (
  currentQuery: Condition | undefined,
  parentId: string
): Condition => {
  // convertQuery flattens nested groups into one value per property, so a nested parent condition must go too.
  const remaining =
    currentQuery && !isEmptyCondition(currentQuery)
      ? withoutProviderParent(currentQuery)
      : undefined;
  const existing = !remaining
    ? []
    : isCombineCondition(remaining) &&
        remaining.operator === ConditionOperatorEnum.And
      ? (remaining.conditions ?? [])
      : [remaining];

  return createCombineCondition([
    ...existing,
    {
      property: providerParentPropertyName,
      operator: ConditionOperatorEnum.In,
      value: [parentId],
    },
  ]);
};
